import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, FormControl, Select, MenuItem, Typography, List, ListItem } from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import { toast } from 'react-toastify';
import InfoTooltip from '../ClientDetailMain/InfoTooltip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import './BulkUpdateModal.css';

const BulkUpdateModal = ({ onUpdateComplete }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedClientsData = location.state?.selectedClientsData || [];
    
    const [icp, setIcp] = useState('');
    const [touchpointFrequency, setTouchpointFrequency] = useState('');
    const [billingStatus, setBillingStatus] = useState('');
    const [lastContacted, setLastContacted] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [selectedPrograms, setSelectedPrograms] = useState([]);
    const [selectedCSM, setSelectedCSM] = useState('');
    const [availableCSMs, setAvailableCSMs] = useState([]);
    const [onboardingData, setOnboardingData] = useState({
        contractSigned: "",
        welcomeCall: "",
        onboardingSurvey: "",
        onboardingCall: "",
        groupAccess: ""
    });
    const [onboardingError, setOnboardingError] = useState(false);
    const [onboardingSettings, setOnboardingSettings] = useState([]);
    const [onboardingSettingsLoaded, setOnboardingSettingsLoaded] = useState(false);

    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;

    // Tooltip configurations
    const tooltipConfig = {
        iconSx: { fontSize: '16px', color: '#888', marginLeft: '6px' },
        programs: "Select one or more programs. The assigned programs will completely replace the client's previous programs.",
        onboarding: "It's possible to activate or deactivate onboarding processes, which will replace the previous ones assigned to selected clients."
    };

    // Common styles for form controls
    const formStyles = {
        // Common Select styles
        selectBase: {
            borderRadius: '6.7px',
            '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                '& fieldset': { border: 'none' },
            },
            '& .MuiSelect-icon': { color: '#000' },
            '& .MuiSelect-select': {
                fontSize: '16px',
                textAlign: 'left',
                paddingLeft: '16px',
            },
        },
        // Typography for labels
        labelStyle: {
            mb: 1, 
            fontWeight: 600, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            color: 'rgba(0, 0, 0, 0.87)'
        },
        // DatePicker styles
        datePickerStyle: {
            backgroundColor: '#f0f0f0',
            borderRadius: '6.7px',
            '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                '& fieldset': { border: 'none' },
            },
            '& .MuiInputBase-input': {
                fontSize: '16px',
                textAlign: 'left',
                paddingLeft: '16px',
            },
        },
        // Button styles
        buttonBase: {
            fontFamily: 'Red Hat Text',
            textTransform: 'capitalize',
            color: 'rgba(255, 255, 255, 1)',
            fontWeight: 500,
            borderRadius: '37px',
            lineHeight: '13.23px',
            textAlign: 'left',
            padding: '14px 25px',
        },
        cancelButton: {
            backgroundColor: '#F52B53',
            '&:hover': { backgroundColor: '#d32f2f' },
        },
        primaryButton: {
            backgroundColor: 'rgba(87, 42, 241, 1)',
            '&:hover': { backgroundColor: '#4A21D1' },
            '&:disabled': { backgroundColor: '#ccc', color: '#666' },
        }
    };

    useEffect(() => {
        const fetchPrograms = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/programs/programs`, { params: { workspaceId, userId } });
                setServiceTypes(data.map(p => ({ id: p.id, description: p.description })));
            } catch (err) {
                setServiceTypes([]);
            }
        };

        const fetchCSMUsers = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/clients/available-csm`, { 
                    params: { workspaceId, adminUserId: userId } 
                });
                setAvailableCSMs(data || []);
            } catch (err) {
                console.error('Error fetching CSM users:', err);
                setAvailableCSMs([]);
            }
        };

        if (workspaceId && userId) {
            fetchPrograms();
            fetchCSMUsers();
        }
    }, [workspaceId, userId]);

    useEffect(() => {
        const fetchOnboardingSettings = async () => {
            if (!workspaceId) return;
            
            try {
                const response = await axios.get(`${API_URL}/admin/onboarding-settings`, {
                    params: { workspaceId }
                });
                if (response.data) {
                    setOnboardingSettings(response.data);
                }
            } catch (error) {
                console.error('Error fetching onboarding settings:', error);
                setOnboardingSettings([]);
            } finally {
                setOnboardingSettingsLoaded(true);
            }
        };

        fetchOnboardingSettings();
    }, [workspaceId]);

    const getBillingColor = (status) => {
        switch (status) {
            case 'Active':
                return { backgroundColor: '#0AC67D1F', textColor: '#03BC72' };
            case 'Paused':
                return { backgroundColor: '#F4C1071F', textColor: '#F6A637' };
            case 'Past Due':
                return { backgroundColor: '#FF00001F', textColor: '#F52B53' };
            default:
                return { backgroundColor: '#F5F5F5', textColor: '#666666' };
        }
    };

    const { backgroundColor, textColor } = getBillingColor(billingStatus);

    const handleUpdate = async () => {
        if (!onboardingSettingsLoaded) {
            toast.error('Please wait for onboarding settings to load');
            return;
        }

        const basicUpdatesEmpty = icp === '' && touchpointFrequency === '' && billingStatus === '' && !lastContacted;
        const visibleOnboardingValues = getVisibleOnboardingValues();
        const onboardingEmpty = visibleOnboardingValues.length === 0 || visibleOnboardingValues.every(val => val === "");
        
        if (basicUpdatesEmpty && onboardingEmpty && selectedPrograms.length === 0 && selectedCSM === '') {
            toast.error('Please select at least one field, program, CSM assignment, or onboarding update to update');
            return;
        }

        setIsUpdating(true);

        try {
            if (!basicUpdatesEmpty) {
                const updates = selectedClientsData.map(client => {
                    const update = { id: client.id };
                    if (icp !== '') update.icp = icp;
                    if (touchpointFrequency !== '') update.touchpointFrequency = touchpointFrequency;
                    if (billingStatus !== '') update.billingStatus = billingStatus;
                    if (lastContacted) update.lastContacted = lastContacted.toISOString();
                    return update;
                });

                const payload = {
                    workspaceId: Number(workspaceId),
                    updates: updates
                };

                await axios.put(`${API_URL}/clients/bulk-update-clients`, payload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }
            
            if (selectedPrograms.length > 0) {
                const programPayload = {
                    workspaceId: Number(workspaceId),
                    programIds: selectedPrograms.map(id => Number(id)),
                    clientIds: selectedClientsData.map(client => client.id)
                };
                await axios.post(`${API_URL}/programs/assign-program-to-clients`, programPayload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            // CSM Assignment
            if (selectedCSM !== '') {
                const csmUpdates = selectedClientsData.map(client => ({
                    id: client.id,
                    userId: selectedCSM === 'null' ? null : Number(selectedCSM)
                }));

                const csmPayload = {
                    workspaceId: Number(workspaceId),
                    updates: csmUpdates
                };

                await axios.put(`${API_URL}/clients/bulk-update-clients`, csmPayload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }
            
            if (selectedClientsData.length > 0 && visibleOnboardingValues.length > 0 && visibleOnboardingValues.some(val => val !== "")) {
                const allModified = visibleOnboardingValues.every(val => val !== "");
                if (!allModified) {
                    setOnboardingError(true);
                    toast.error('Please complete all onboarding fields or leave them all as "No Change"');
                    setIsUpdating(false);
                    return;
                } else {
                    setOnboardingError(false);
                }
                const updatedOnboarding = {
                    contractSigned: onboardingData.contractSigned === "true",
                    welcomeCall: onboardingData.welcomeCall === "true",
                    onboardingSurvey: onboardingData.onboardingSurvey === "true",
                    onboardingCall: onboardingData.onboardingCall === "true",
                    groupAccess: onboardingData.groupAccess === "true"
                };
                const onboardingPayload = {
                    workspaceId: String(workspaceId),
                    clientIds: selectedClientsData.map(client => String(client.id)),
                    data: updatedOnboarding
                };
                await axios.post(`${API_URL}/onboarding/assign-onboarding-to-clients`, onboardingPayload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            setTimeout(() => {
                toast.success(`Successfully updated ${selectedClientsData.length} client(s)`);
                if (typeof onUpdateComplete === 'function') {
                    onUpdateComplete();
                }
                setIcp('');
                setTouchpointFrequency('');
                setBillingStatus('');
                setLastContacted(null);
                setSelectedCSM('');
                setOnboardingData({
                    contractSigned: "",
                    welcomeCall: "",
                    onboardingSurvey: "",
                    onboardingCall: "",
                    groupAccess: ""
                });
                setSelectedPrograms([]);
                setIsUpdating(false);
                navigate(-1);
            }, 3000);
            
        } catch (error) {
            console.error('Error updating clients:', error);
            toast.error('Error updating clients. Please try again.');
            setIsUpdating(false);
        }
    };

    const handleClose = () => {
        navigate(-1);
    };

    const toggleBulkStep = (frontendSlug) => {
        setOnboardingData(prev => {
            let newVal = "";
            if (prev[frontendSlug] === "") newVal = "true";
            else if (prev[frontendSlug] === "true") newVal = "false";
            else if (prev[frontendSlug] === "false") newVal = "";
            return { ...prev, [frontendSlug]: newVal };
        });
    };

    // Unified onboarding step configuration with consistent mapping
    const onboardingStepMapping = [
        { 
            frontendSlug: 'contractSigned', 
            apiSlug: 'contract_signed', 
            label: 'Contract Signed', 
            getValue: () => onboardingData.contractSigned 
        },
        { 
            frontendSlug: 'welcomeCall', 
            apiSlug: 'welcome_call', 
            label: 'Welcome Call', 
            getValue: () => onboardingData.welcomeCall 
        },
        { 
            frontendSlug: 'onboardingSurvey', 
            apiSlug: 'onboarding_survey', 
            label: 'Onboarding Survey', 
            getValue: () => onboardingData.onboardingSurvey 
        },
        { 
            frontendSlug: 'onboardingCall', 
            apiSlug: 'onboarding_call', 
            label: 'Onboarding Call', 
            getValue: () => onboardingData.onboardingCall 
        },
        { 
            frontendSlug: 'groupAccess', 
            apiSlug: 'group_access', 
            label: 'Group Access', 
            getValue: () => onboardingData.groupAccess 
        }
    ];

    // Filter only enabled onboarding steps based on settings
    const visibleBulkSteps = onboardingSettingsLoaded ? onboardingStepMapping.filter(step => {
        const setting = onboardingSettings.find(s => s.slug === step.apiSlug);
        return setting ? setting.enabled : true;
    }) : [];

    // Helper function to check onboarding data for visible steps only
    const getVisibleOnboardingValues = () => {
        if (!onboardingSettingsLoaded || visibleBulkSteps.length === 0) {
            return [];
        }
        return visibleBulkSteps.map(step => step.getValue());
    };

    const getVisibleOnboardingItems = () => {
        if (!onboardingSettingsLoaded) return [];
        
        const allOnboardingItems = [
            { field: 'contract_signed', label: 'Contract Signed', apiSlug: 'contract_signed' },
            { field: 'welcome_call', label: 'Welcome Call', apiSlug: 'welcome_call' },
            { field: 'onboarding_survey', label: 'Onboarding Survey', apiSlug: 'onboarding_survey' },
            { field: 'onboarding_call', label: 'Onboarding Call', apiSlug: 'onboarding_call' },
            { field: 'group_access', label: 'Group Access', apiSlug: 'group_access' }
        ];

        return allOnboardingItems.filter(item => {
            const setting = onboardingSettings.find(s => s.slug === item.apiSlug);
            return setting ? setting.enabled : true;
        });
    };

    const handleProgramsChange = (e) => {
        setSelectedPrograms(e.target.value);
    };

    return (
        <div className="bulk-update-page responsive-container" style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
                <h1>Bulk Update Clients</h1>
                <Typography variant="body2" color="text.secondary">
                    Update {selectedClientsData.length} selected client(s)
                </Typography>
            </div>
            <Box sx={{ display: 'flex', gap: 2 }} className="main-content-container">
                {/* Left column */}
                <Box sx={{ flex: 1 }} className="left-column">
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Update Fields</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }} className="update-fields-grid">
                            <FormControl fullWidth>
                                <Typography variant="body2" sx={formStyles.labelStyle}>ICP Status</Typography>
                                <Select 
                                    value={icp} 
                                    onChange={(e) => setIcp(e.target.value)} 
                                    displayEmpty 
                                    sx={{ 
                                        ...formStyles.selectBase,
                                        backgroundColor: '#0AC67D1F',
                                    }}
                                >
                                    <MenuItem value="">No Change</MenuItem>
                                    <MenuItem value="ICP I">ICP I</MenuItem>
                                    <MenuItem value="ICP II">ICP II</MenuItem>
                                    <MenuItem value="ICP III">ICP III</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <Typography variant="body2" sx={formStyles.labelStyle}>Touchpoint Frequency</Typography>
                                <Select 
                                    value={touchpointFrequency} 
                                    onChange={(e) => setTouchpointFrequency(e.target.value)} 
                                    displayEmpty 
                                    sx={{ 
                                        ...formStyles.selectBase,
                                        backgroundColor: '#F4C1071F',
                                    }}
                                >
                                    <MenuItem value="">No Change</MenuItem>
                                    <MenuItem value="Weekly">Weekly</MenuItem>
                                    <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                                    <MenuItem value="Monthly">Monthly</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <Typography variant="body2" sx={formStyles.labelStyle}>Billing Status</Typography>
                                <Select 
                                    value={billingStatus} 
                                    onChange={(e) => setBillingStatus(e.target.value)} 
                                    displayEmpty 
                                    sx={{ 
                                        ...formStyles.selectBase,
                                        backgroundColor: backgroundColor,
                                        color: textColor,
                                    }}
                                >
                                    <MenuItem value="">No Change</MenuItem>
                                    <MenuItem value="Active" style={{ color: '#03BC72' }}>Active</MenuItem>
                                    <MenuItem value="Paused" style={{ color: '#F6A637' }}>Paused</MenuItem>
                                    <MenuItem value="Past Due" style={{ color: '#F52B53' }}>Past Due</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography variant="body2" sx={formStyles.labelStyle}>
                                Programs/Service Types
                                <InfoTooltip
                                    title={tooltipConfig.programs}
                                    iconSx={tooltipConfig.iconSx}
                                />
                            </Typography>
                            <Select 
                                multiple
                                value={selectedPrograms}
                                onChange={handleProgramsChange}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return "No Change";
                                    }
                                    const selectedNames = serviceTypes
                                        .filter(p => selected.includes(p.id))
                                        .map(p => p.description);
                                    const fullText = selectedNames.join(', ');
                                    
                                    if (fullText.length > 40) {
                                        return fullText.substring(0, 37) + '...';
                                    }
                                    return fullText;
                                }}
                                sx={{
                                    ...formStyles.selectBase,
                                    backgroundColor: '#f0f0f0',
                                    '& .MuiSelect-select': {
                                        ...formStyles.selectBase['& .MuiSelect-select'],
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%',
                                    },
                                }}
                            >
                                {serviceTypes.map((p) => (
                                    <MenuItem 
                                        key={p.id} 
                                        value={p.id}
                                        sx={{
                                            "&.Mui-selected": {
                                                backgroundColor: "#d0f0d0 !important",
                                            }
                                        }}
                                    >
                                        {p.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography variant="body2" sx={formStyles.labelStyle}>
                                Assigned CSM
                            </Typography>
                            <Select 
                                value={selectedCSM}
                                onChange={(e) => setSelectedCSM(e.target.value)}
                                displayEmpty
                                sx={{
                                    ...formStyles.selectBase,
                                    backgroundColor: '#e8f4fd',
                                }}
                            >
                                <MenuItem value="">No Change</MenuItem>
                                <MenuItem value="null">Remove CSM</MenuItem>
                                {availableCSMs.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.firstName && user.lastName 
                                            ? `${user.firstName} ${user.lastName}`
                                            : user.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography variant="body2" sx={formStyles.labelStyle}>
                                Last Contacted On
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={lastContacted ? dayjs(lastContacted) : null}
                                    onChange={(newDate) => setLastContacted(newDate)}
                                    format="DD/MM/YYYY"
                                    sx={formStyles.datePickerStyle}
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography variant="body2" sx={formStyles.labelStyle}>
                                Onboarding Updates
                                <InfoTooltip
                                    title={tooltipConfig.onboarding}
                                    iconSx={tooltipConfig.iconSx}
                                />
                            </Typography>
                            <div className="onboarding-flow responsive-onboarding" style={{ padding: '10px', borderRadius: '8px', marginRight: '15px', marginLeft: '-10px' }}>
                                <div className="onboarding-steps responsive-steps" style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap' }}>
                                    {!onboardingSettingsLoaded ? (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            minHeight: '90px',
                                            width: '100%',
                                            color: '#666',
                                            fontSize: '14px'
                                        }}>
                                            Loading onboarding options...
                                        </div>
                                    ) : visibleBulkSteps.length === 0 ? (
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            minHeight: '90px',
                                            width: '100%',
                                            color: '#666',
                                            fontSize: '14px'
                                        }}>
                                            No onboarding steps enabled
                                        </div>
                                    ) : visibleBulkSteps.map((step, index) => (
                                        <div
                                            key={index}
                                            className={`onboarding-step ${step.getValue() === "true" ? 'completed' : step.getValue() === "false" ? 'incomplete' : ''}`}
                                            onClick={() => toggleBulkStep(step.frontendSlug)}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '10px',
                                                border: (onboardingError && step.getValue() === "") ? '2px solid red' : '1px solid #ccc',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexDirection: 'column',
                                                minWidth: '90px'
                                            }}
                                        >
                                            <div className="status-icon" style={{ marginBottom: '5px' }}>
                                                {step.getValue() === "" ? (
                                                    <span style={{ fontSize: '0.8rem', color: '#555' }}>No Change</span>
                                                ) : step.getValue() === "true" ? (
                                                    <img style={{ width: '60%' }} src="/green_check.png" alt="Completed" />
                                                ) : (
                                                    <img style={{ width: '66%' }} src="/x_icon.png" alt="Not Completed" />
                                                )}
                                            </div>
                                            <span className="step-label" style={{ 
                                                fontSize: '0.85rem', 
                                                textAlign: 'center',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                                lineHeight: '1.2',
                                                maxWidth: '90px'
                                            }}>
                                                {step.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </FormControl>
                    </Box>
                </Box>
                {/* Right column - Selected Clients */}
                <Box sx={{ flex: 1.6 }} className="right-column">
                    <Typography variant="h6" sx={{ mb: 2, marginTop: '-80px' }} className="clients-header">
                        Selected Clients ({selectedClientsData.length})
                    </Typography>
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1.5fr',
                        mb: 1,
                        px: 2.5,
                        py: 0,
                        bgcolor: '#572AF1',
                        borderRadius: 1,
                        border: '1px solid #e9ecef'
                    }} className="clients-grid-header">
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>Client Name</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>ICP Status</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>Touchpoint Frequency</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>Billing Status</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ffffff' }}>Assigned CSM</Typography>
                    </Box>
                    <List sx={{ 
                        maxHeight: 510,
                        overflow: 'auto', 
                        bgcolor: '#f8f9fa', 
                        borderRadius: 2,
                        border: '1px solid #e9ecef',
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb': { 
                            background: 'rgba(87, 42, 241, 0.8)',
                            borderRadius: '4px',
                            '&:hover': { background: 'rgba(87, 42, 241, 1)' },
                        },
                    }} className="clients-list-container">
                        {selectedClientsData.map((client, index) => {
                            return (
                                <ListItem 
                                    key={client.id} 
                                    sx={{ 
                                        py: 1.5,
                                        px: 2.5,
                                        borderBottom: index < selectedClientsData.length - 1 ? '1px solid #e9ecef' : 'none',
                                        '&:hover': {
                                            backgroundColor: '#f8f9fa',
                                        },
                                        display: 'grid',
                                        gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1.5fr',
                                        gap: 2,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 600, 
                                            color: '#291B3B', 
                                            fontSize: '20px',
                                            fontFamily: 'Albert Sans',
                                            lineHeight: '21.66px',
                                            letterSpacing: '-0.03em',
                                            textAlign: 'left'
                                        }}>
                                            {`${client.firstName} ${client.lastName}`}
                                        </Typography>
                                        <Typography variant="body2" sx={{ 
                                            color: '#291B3B', 
                                            fontSize: '16px',
                                            fontFamily: 'Albert Sans',
                                            fontWeight: 600,
                                            lineHeight: '22px',
                                            letterSpacing: '-3%',
                                            opacity: 0.5,
                                            marginTop: '5px'
                                        }}>
                                            {client.companyName ? `@${client.companyName}` : 'No company'}
                                        </Typography>
                                    </Box>
                                    
                                    {/* ICP Status */}
                                    <Typography variant="body2" sx={{ 
                                        color: client.icp ? '#291B3B' : '#999', 
                                        fontStyle: client.icp ? 'normal' : 'italic',
                                        fontSize: '14px'
                                    }}>
                                        {client.icp || 'Not set'}
                                    </Typography>
                                    
                                    {/* Touchpoint Frequency */}
                                    <Typography variant="body2" sx={{ 
                                        color: client.touchpointFrequency ? '#291B3B' : '#999', 
                                        fontStyle: client.touchpointFrequency ? 'normal' : 'italic',
                                        fontSize: '14px'
                                    }}>
                                        {client.touchpointFrequency || 'Not set'}
                                    </Typography>
                                    
                                    {/* Billing Status */}
                                    <Typography variant="body2" sx={{ 
                                        color: client.billingStatus ? '#291B3B' : '#999', 
                                        fontStyle: client.billingStatus ? 'normal' : 'italic',
                                        fontSize: '14px'
                                    }}>
                                        {client.billingStatus || 'Not set'}
                                    </Typography>
                                    
                                    {/* Assigned CSM */}
                                    <Typography variant="body2" sx={{ 
                                        color: client.csmName ? '#291B3B' : '#999', 
                                        fontStyle: client.csmName ? 'normal' : 'italic',
                                        fontSize: '14px'
                                    }}>
                                        {client.csmName ? client.csmName.split(' ').slice(0, 2).join(' ') : 'Not assigned'}
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Box>
            <Box className="action-buttons" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: '-30px' }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        ...formStyles.buttonBase,
                        ...formStyles.cancelButton,
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleUpdate} 
                    disabled={
                        isUpdating ||
                        !onboardingSettingsLoaded || // Disable until settings are loaded
                        (icp === '' && touchpointFrequency === '' && billingStatus === '' && !lastContacted && selectedPrograms.length === 0 && selectedCSM === '' &&
                        getVisibleOnboardingValues().every(val => val === ""))
                    }
                    sx={{
                        ...formStyles.buttonBase,
                        ...formStyles.primaryButton,
                    }}
                >
                    {isUpdating ? 'Updating...' : onboardingSettingsLoaded ? 'Update Clients' : 'Loading...'}
                </Button>
            </Box>
        </div>
    );
};

export default BulkUpdateModal;